import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';

interface ThreadMessage {
  id: string;
  from: 'client' | 'pro';
  text: string;
  ts: string;
}

interface ThreadResponse {
  messages: ThreadMessage[];
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export default function DemoProRoute() {
  const [params] = useSearchParams();
  const clientPhone = params.get('phone') ?? '+33612345678';
  const clientName = params.get('client') ?? 'Yanis (client)';

  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [now, setNow] = useState(new Date());
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const out = await api<ThreadResponse>(
          `/demo/thread?clientPhone=${encodeURIComponent(clientPhone)}`,
        );
        if (!cancelled) {
          setMessages(out.messages);
          setNow(new Date());
        }
      } catch {
        // silent
      }
    }
    void poll();
    const id = setInterval(() => void poll(), 1500);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [clientPhone]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  return (
    <div className="fixed inset-0 flex flex-col bg-[var(--background)]">
      {/* Pro cockpit header — Classic Barber design */}
      <header className="bg-[var(--surface)] border-b border-[var(--border)] pt-3 pb-3 px-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-sans text-[11px] uppercase tracking-wider text-[var(--text-faint)]">
              Cockpit Karim · Mode Auto
            </div>
            <div className="font-sans text-[17px] font-semibold text-[var(--text)] mt-0.5">
              Fil avec {clientName}
            </div>
            <div className="font-mono text-[10px] text-[var(--text-muted)] mt-1">
              {clientPhone} · live polling 1.5s · {now.toLocaleTimeString('fr-FR')}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="px-2 py-0.5 rounded-full bg-[var(--success)]/15 text-[var(--success)] font-mono text-[10px] uppercase tracking-wider">
              ● auto
            </span>
            <span className="text-[var(--text-faint)] font-mono text-[10px]">
              {messages.length} msg
            </span>
          </div>
        </div>
      </header>

      {/* Pro perspective: pro = right (own), client = left (incoming) */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-2">
        {messages.length === 0 && (
          <div className="text-center mt-12">
            <p className="text-[var(--text-muted)] text-sm font-sans">
              En attente d'un message client.
            </p>
            <p className="text-[var(--text-faint)] text-[11px] font-mono mt-2">
              Écris depuis le second simulateur, ça s'affichera ici.
            </p>
          </div>
        )}
        {messages.map((m, idx) => {
          const prev = messages[idx - 1];
          const showTime = !prev || new Date(m.ts).getTime() - new Date(prev.ts).getTime() > 60_000;
          const isPro = m.from === 'pro';
          return (
            <div key={m.id}>
              {showTime && (
                <div className="text-center text-[10px] text-[var(--text-faint)] font-mono py-2">
                  {formatTime(m.ts)}
                </div>
              )}
              <div className={`flex ${isPro ? 'justify-end' : 'justify-start'}`}>
                <div className="max-w-[78%] flex flex-col gap-1">
                  <div
                    className={`px-3 py-2 rounded-2xl text-[14px] font-sans leading-snug ${
                      isPro
                        ? 'bg-[var(--success)] text-white rounded-br-md'
                        : 'bg-[var(--elevated)] text-[var(--text)] rounded-bl-md border border-[var(--border)]'
                    }`}
                  >
                    {m.text}
                  </div>
                  {isPro && (
                    <div className="text-right">
                      <span className="font-mono text-[9px] text-[var(--text-faint)] uppercase tracking-wider">
                        envoyé par BOOKRCS
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pro footer: no input — Karim's auto mode handles replies */}
      <footer className="bg-[var(--surface)] border-t border-[var(--border)] px-4 py-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[var(--success)] animate-pulse" />
            <span className="font-mono text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
              Auto-réponse active
            </span>
          </div>
          <span className="font-mono text-[10px] text-[var(--text-faint)]">
            Ollama qwen2.5-coder · local
          </span>
        </div>
      </footer>
    </div>
  );
}
